/**
 * Save a file the browser is showing.
 *
 * The `download` attribute on an anchor is ignored when the href is
 * cross-origin, and stored files live on Supabase storage rather than on this
 * origin — so `<a href={signedUrl} download>` opens the file in a tab instead
 * of saving it, no matter what the anchor says. Two ways around that:
 *
 *  * Ask storage for `Content-Disposition: attachment` when minting the signed
 *    URL. Best when the caller knows the stored path, which is what
 *    `api.fileUrl(path, { download })` returns.
 *  * Fetch the bytes and hand the browser a same-origin blob URL, where the
 *    attribute is honoured. Works from a display URL alone, at the cost of
 *    holding the file in memory.
 *
 * This is the second. Callers that have the path should prefer the first.
 */
export async function saveFile(url, filename) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not download the file (${response.status}).`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Give the click a tick to start before releasing the blob.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  }
}
